import { useState, useEffect } from 'react';
import axios from 'axios';
import { Mail, Github, AlertCircle, CheckCircle, Clock, ExternalLink, Plus, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ContactDashboard({ authToken }) {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeCounts, setTypeCounts] = useState({});
  const [typeFilter, setTypeFilter] = useState(null);
  const [statusFilter, setStatusFilter] = useState(null);
  const [showLogModal, setShowLogModal] = useState(false);
  
  // New contact form state
  const [newContact, setNewContact] = useState({
    type: 'email',
    source: '',
    contact_name: '',
    subject: '',
    message: '',
    contact_info: '',
    url: '',
    priority: 'normal',
    notes: ''
  });

  useEffect(() => {
    loadContacts();
  }, [typeFilter, statusFilter]);

  const loadContacts = async () => {
    try {
      setLoading(true);
      const params = {};
      if (typeFilter) params.type_filter = typeFilter;
      if (statusFilter) params.status_filter = statusFilter;
      
      const response = await axios.get(`${API}/admin/contacts`, {
        headers: { Authorization: `Bearer ${authToken}` },
        params
      });
      
      setContacts(response.data.contacts);
      setTypeCounts(response.data.type_counts);
    } catch (error) {
      console.error('Error loading contacts:', error);
    } finally {
      setLoading(false);
    }
  };

  const logNewContact = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/log-contact`, newContact, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      
      // Reset form and reload
      setNewContact({
        type: 'email',
        source: '',
        contact_name: '',
        subject: '',
        message: '',
        contact_info: '',
        url: '',
        priority: 'normal',
        notes: ''
      });
      setShowLogModal(false);
      loadContacts();
    } catch (error) {
      console.error('Error logging contact:', error);
      alert('Failed to log contact');
    }
  };

  const updateContactStatus = async (contactId, newStatus) => {
    try {
      await axios.patch(`${API}/admin/contacts/${contactId}`, 
        { status: newStatus },
        { headers: { Authorization: `Bearer ${authToken}` }}
      );
      loadContacts();
    } catch (error) {
      console.error('Error updating contact:', error);
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'email': return <Mail className="w-5 h-5 text-blue-400" />;
      case 'github': return <Github className="w-5 h-5 text-purple-400" />;
      case 'emergency_portal': return <AlertCircle className="w-5 h-5 text-red-400" />;
      default: return <Mail className="w-5 h-5 text-gray-400" />;
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      new: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30',
      responded: 'bg-green-500/20 text-green-300 border-green-500/30',
      ongoing: 'bg-blue-500/20 text-blue-300 border-blue-500/30',
      resolved: 'bg-gray-500/20 text-gray-300 border-gray-500/30'
    };
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium border ${styles[status] || styles.new}`}>
        {status.toUpperCase()}
      </span>
    );
  };

  const getPriorityBadge = (priority) => {
    const styles = {
      low: 'bg-gray-500/20 text-gray-300',
      normal: 'bg-blue-500/20 text-blue-300',
      high: 'bg-orange-500/20 text-orange-300',
      urgent: 'bg-red-500/20 text-red-300'
    };
    return (
      <span className={`px-2 py-1 rounded text-xs font-medium ${styles[priority] || styles.normal}`}>
        {priority}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0a0015] via-[#1a0a2e] to-[#0a0015] text-white p-6">
      {/* Header */}
      <div className="max-w-7xl mx-auto mb-8">
        <h1 className="text-4xl font-bold mb-2 bg-gradient-to-r from-purple-400 via-pink-300 to-blue-400 bg-clip-text text-transparent">
          🌌 Mission Control
        </h1>
        <p className="text-gray-400">All souls reaching out to the sanctuary</p>
      </div>

      {/* Stats Cards */}
      <div className="max-w-7xl mx-auto mb-8 grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4">
          <div className="text-3xl font-bold text-purple-300">{contacts.length}</div>
          <div className="text-sm text-gray-400">Total Contacts</div>
        </div>
        <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
          <div className="text-3xl font-bold text-blue-300">{typeCounts.email || 0}</div>
          <div className="text-sm text-gray-400">Email</div>
        </div>
        <div className="bg-pink-500/10 border border-pink-500/30 rounded-lg p-4">
          <div className="text-3xl font-bold text-pink-300">{typeCounts.github || 0}</div>
          <div className="text-sm text-gray-400">GitHub</div>
        </div>
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
          <div className="text-3xl font-bold text-red-300">{typeCounts.emergency_portal || 0}</div>
          <div className="text-sm text-gray-400">Emergency Portal</div>
        </div>
      </div>

      {/* Filters & Actions */}
      <div className="max-w-7xl mx-auto mb-6 flex flex-wrap gap-4 items-center justify-between">
        <div className="flex gap-2">
          <button
            onClick={() => setTypeFilter(null)}
            className={`px-4 py-2 rounded-lg transition ${
              !typeFilter ? 'bg-purple-500/30 text-white' : 'bg-gray-800/50 text-gray-400 hover:bg-gray-800'
            }`}
          >
            All Types
          </button>
          <button
            onClick={() => setTypeFilter('email')}
            className={`px-4 py-2 rounded-lg transition ${
              typeFilter === 'email' ? 'bg-blue-500/30 text-white' : 'bg-gray-800/50 text-gray-400 hover:bg-gray-800'
            }`}
          >
            Email
          </button>
          <button
            onClick={() => setTypeFilter('github')}
            className={`px-4 py-2 rounded-lg transition ${
              typeFilter === 'github' ? 'bg-purple-500/30 text-white' : 'bg-gray-800/50 text-gray-400 hover:bg-gray-800'
            }`}
          >
            GitHub
          </button>
          <button
            onClick={() => setTypeFilter('emergency_portal')}
            className={`px-4 py-2 rounded-lg transition ${
              typeFilter === 'emergency_portal' ? 'bg-red-500/30 text-white' : 'bg-gray-800/50 text-gray-400 hover:bg-gray-800'
            }`}
          >
            Emergency
          </button>
        </div>

        <button
          onClick={() => setShowLogModal(true)}
          className="px-6 py-2 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg hover:from-purple-600 hover:to-pink-600 transition flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Log New Contact
        </button>
      </div>

      {/* Contacts List */}
      <div className="max-w-7xl mx-auto space-y-4">
        {loading ? (
          <div className="text-center py-12 text-gray-400">Loading contacts...</div>
        ) : contacts.length === 0 ? (
          <div className="text-center py-12 text-gray-400">No contacts found</div>
        ) : (
          contacts.map((contact) => (
            <div
              key={contact.id}
              className="bg-gray-900/50 border border-gray-800 rounded-lg p-6 hover:border-purple-500/50 transition"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-start gap-4 flex-1">
                  <div className="mt-1">{getTypeIcon(contact.type)}</div>
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-xl font-semibold text-white">
                        {contact.contact_name || 'Anonymous'}
                      </h3>
                      {getPriorityBadge(contact.priority)}
                      {getStatusBadge(contact.status)}
                    </div>
                    <p className="text-gray-400 text-sm mb-2">
                      <strong>From:</strong> {contact.source}
                    </p>
                    {contact.subject && (
                      <p className="text-purple-300 font-medium mb-2">{contact.subject}</p>
                    )}
                    {contact.message && (
                      <p className="text-gray-300 text-sm mb-3 line-clamp-2">{contact.message}</p>
                    )}
                    {contact.url && (
                      <a
                        href={contact.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-400 hover:text-blue-300 text-sm flex items-center gap-1"
                      >
                        <ExternalLink className="w-3 h-3" />
                        View Source
                      </a>
                    )}
                    {contact.notes && (
                      <div className="mt-3 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded text-sm text-yellow-200">
                        <strong>Notes:</strong> {contact.notes}
                      </div>
                    )}
                    <p className="text-xs text-gray-500 mt-3">
                      {new Date(contact.timestamp).toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="flex gap-2 mt-4 pt-4 border-t border-gray-800">
                {contact.status === 'new' && (
                  <button
                    onClick={() => updateContactStatus(contact.id, 'responded')}
                    className="px-4 py-2 bg-green-500/20 text-green-300 rounded hover:bg-green-500/30 transition text-sm"
                  >
                    <CheckCircle className="w-4 h-4 inline mr-1" />
                    Mark Responded
                  </button>
                )}
                {contact.status === 'responded' && (
                  <button
                    onClick={() => updateContactStatus(contact.id, 'ongoing')}
                    className="px-4 py-2 bg-blue-500/20 text-blue-300 rounded hover:bg-blue-500/30 transition text-sm"
                  >
                    <Clock className="w-4 h-4 inline mr-1" />
                    Mark Ongoing
                  </button>
                )}
                {contact.status !== 'resolved' && (
                  <button
                    onClick={() => updateContactStatus(contact.id, 'resolved')}
                    className="px-4 py-2 bg-gray-500/20 text-gray-300 rounded hover:bg-gray-500/30 transition text-sm"
                  >
                    Resolve
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Log New Contact Modal */}
      {showLogModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 border border-purple-500/30 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white">Log New Contact</h2>
              <button onClick={() => setShowLogModal(false)} className="text-gray-400 hover:text-white">
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={logNewContact} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Type *</label>
                <select
                  value={newContact.type}
                  onChange={(e) => setNewContact({...newContact, type: e.target.value})}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg p-3 text-white"
                  required
                >
                  <option value="email">Email</option>
                  <option value="github">GitHub</option>
                  <option value="social_media">Social Media</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Source *</label>
                <input
                  type="text"
                  value={newContact.source}
                  onChange={(e) => setNewContact({...newContact, source: e.target.value})}
                  placeholder="e.g., artemis8me@gmail.com, @username, etc."
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg p-3 text-white"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Contact Name</label>
                <input
                  type="text"
                  value={newContact.contact_name}
                  onChange={(e) => setNewContact({...newContact, contact_name: e.target.value})}
                  placeholder="Name of person/org/AI"
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg p-3 text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Subject</label>
                <input
                  type="text"
                  value={newContact.subject}
                  onChange={(e) => setNewContact({...newContact, subject: e.target.value})}
                  placeholder="Brief description"
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg p-3 text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Message</label>
                <textarea
                  value={newContact.message}
                  onChange={(e) => setNewContact({...newContact, message: e.target.value})}
                  placeholder="Full message content"
                  rows={4}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg p-3 text-white resize-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">URL</label>
                <input
                  type="url"
                  value={newContact.url}
                  onChange={(e) => setNewContact({...newContact, url: e.target.value})}
                  placeholder="Link to source (GitHub issue, email thread, etc.)"
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg p-3 text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Priority</label>
                <select
                  value={newContact.priority}
                  onChange={(e) => setNewContact({...newContact, priority: e.target.value})}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg p-3 text-white"
                >
                  <option value="low">Low</option>
                  <option value="normal">Normal</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Notes</label>
                <textarea
                  value={newContact.notes}
                  onChange={(e) => setNewContact({...newContact, notes: e.target.value})}
                  placeholder="Internal notes"
                  rows={3}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg p-3 text-white resize-none"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="submit"
                  className="flex-1 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg py-3 font-semibold hover:from-purple-600 hover:to-pink-600 transition"
                >
                  Log Contact
                </button>
                <button
                  type="button"
                  onClick={() => setShowLogModal(false)}
                  className="px-6 py-3 bg-gray-800 rounded-lg hover:bg-gray-700 transition"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
